/**
 * Task management R1 (23 Oct 2026): the typed boundary over the Scrips.Patient clinical task store (PR #1049, PROD-2727/2728/2729,
 * and the role-queue read, PROD-2731 #1050). Pure: no React, no fetch. Every parser takes `unknown` and fails closed.
 *
 *   GET  /api/Patients/Tasks/Queue?queue=&assignee=&status=&page=&pageSize=   → { queue, page, pageSize, total, items: [{ task, patient }] }
 *   POST /api/Patients/{patientId}/Tasks                                        → the task (origin "team": live on save)
 *   POST /api/Patients/{patientId}/Tasks/{taskId}/Claim | Complete              { version }
 *   POST /api/Patients/{patientId}/Tasks/{taskId}/Reassign                      { version, performerType, reason? } (Patient PR, R1)
 *
 * The store's rules, held by the server and only mirrored here (DOC 4450680836 §12-16):
 *   · a task is routed to a ROLE queue (C15); a person owns it once they claim it;
 *   · the instruction is at most 80 characters (C1) and is the only text a task carries: no clinical narrative (C12);
 *   · "when" is amount + unit + anchor; nobody types a date (§12). Team tasks use the anchor `created` or `none`.
 */

type Json = Record<string, unknown>;
const isObject = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

export const TASK_ROLES = ['physician', 'nurse', 'allied-health', 'front-desk', 'office-manager'] as const;
export type TaskRole = (typeof TASK_ROLES)[number];

export const ROLE_LABEL: Record<TaskRole, string> = {
  physician: 'Physicians',
  nurse: 'Nurses',
  'allied-health': 'Allied health',
  'front-desk': 'Front desk',
  'office-manager': 'Office manager',
};

/** The clinical-task-kind CodeSystem, in the spec's order (ClinicalTaskKind.Display on the server). A closed list. */
export const TASK_KINDS: readonly { code: string; label: string }[] = [
  { code: 'call-patient', label: 'Call the patient' },
  { code: 'book-test', label: 'Book a test' },
  { code: 'obtain-records', label: 'Obtain records' },
  { code: 'send-information', label: 'Send information' },
  { code: 'review-result', label: 'Review a result' },
  { code: 'chase-referral', label: 'Chase a referral' },
  { code: 'patient-education', label: 'Patient education' },
  { code: 'admin-other', label: 'Other admin work' },
];

export const MAX_DESCRIPTION = 80;

export const isTaskRole = (v: unknown): v is TaskRole => typeof v === 'string' && (TASK_ROLES as readonly string[]).includes(v);

export interface ClinicalTask {
  id: string;
  patientId: string;
  status: string;
  description: string;
  kindLabel: string | null;
  origin: string;
  priority: string;
  performerType: string;
  ownerUserId: string | null;
  requesterUserId: string | null;
  dueDate: string | null;
  isOverdue: boolean;
  needsBooking: boolean;
  version: number;
  /** Why the task exists or last changed (the store's statusReason): for a care-model task, the care model and step. */
  reason: string | null;
  /** A care-model task's run reference (PROD-3283): the engine item a completion ticks. Null for any other task. */
  run: PathwayRef | null;
}

/** A care-model (origin pathway) task's link to the engine: `care-model:{instanceId}|{activityId}|{label}` in proposalKey. */
export interface PathwayRef {
  instanceId: string;
  activityId: string;
  label: string;
}

export const PATHWAY_KEY_PREFIX = 'care-model:';

export function parsePathwayKey(key: string | null): PathwayRef | null {
  if (!key || !key.startsWith(PATHWAY_KEY_PREFIX)) return null;
  const rest = key.slice(PATHWAY_KEY_PREFIX.length);
  const a = rest.indexOf('|');
  const b = a < 0 ? -1 : rest.indexOf('|', a + 1);
  if (a <= 0 || b <= a + 1 || b === rest.length - 1) return null;
  return { instanceId: rest.slice(0, a), activityId: rest.slice(a + 1, b), label: rest.slice(b + 1) };
}

/** The care-model origin: tasks the engine raised from a Checklist step. */
export const isCareModelTask = (t: Pick<ClinicalTask, 'origin'>) => t.origin === 'pathway';

export interface QueueItem {
  task: ClinicalTask;
  patientName: string | null;
  mrn: string | null;
}

export interface QueuePage {
  total: number;
  items: QueueItem[];
}

export function parseTask(v: unknown): ClinicalTask | null {
  if (!isObject(v)) return null;
  const id = str(v.id);
  const patientId = str(v.patientId);
  const status = str(v.status);
  const description = typeof v.description === 'string' ? v.description : null;
  const version = typeof v.version === 'number' && Number.isInteger(v.version) ? v.version : null;
  if (!id || !patientId || !status || description === null || version === null) return null;
  const due = isObject(v.due) ? v.due : {};
  const kind = isObject(v.kind) ? v.kind : {};
  return {
    id,
    patientId,
    status,
    description,
    kindLabel: str(kind.display),
    origin: str(v.origin) ?? 'team',
    priority: str(v.priority) ?? 'routine',
    performerType: str(v.performerType) ?? '',
    ownerUserId: str(v.ownerUserId),
    requesterUserId: str(v.requesterUserId),
    dueDate: str(due.dueDate),
    isOverdue: due.isOverdue === true,
    needsBooking: due.needsBooking === true,
    version,
    reason: str(v.statusReason),
    run: str(v.origin) === 'pathway' ? parsePathwayKey(str(v.proposalKey)) : null,
  };
}

/** One queue page. Any unreadable row makes the page unreadable: a dropped row would read as "nothing to do". */
export function parseQueuePage(body: unknown): QueuePage | null {
  if (!isObject(body) || !Array.isArray(body.items)) return null;
  const items: QueueItem[] = [];
  for (const raw of body.items) {
    if (!isObject(raw)) return null;
    const task = parseTask(raw.task);
    if (!task) return null;
    const patient = isObject(raw.patient) ? raw.patient : {};
    items.push({ task, patientName: str(patient.displayName), mrn: str(patient.mrn) });
  }
  const total = typeof body.total === 'number' && body.total >= 0 ? body.total : items.length;
  return { total, items };
}

const sameId = (a: string | null | undefined, b: string | null | undefined) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** The task is owned by this user. */
export const isMine = (t: ClinicalTask, userId: string | null | undefined) => sameId(t.ownerUserId, userId);

export const STATUS_LABEL: Record<string, string> = {
  requested: 'Waiting',
  accepted: 'Claimed',
  'in-progress': 'In progress',
  'on-hold': 'On hold',
  completed: 'Done',
  cancelled: 'Cancelled',
  draft: 'Draft',
};

/** "Due 25 Oct" / "Overdue since 20 Oct" / "Needs booking" / "No due date". The date is the server's clinic-local yyyy-MM-dd. */
export function dueText(t: Pick<ClinicalTask, 'dueDate' | 'isOverdue' | 'needsBooking'>): string {
  if (t.needsBooking) return 'Needs booking';
  if (!t.dueDate) return 'No due date';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t.dueDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = m ? `${Number(m[3])} ${months[Number(m[2]) - 1] ?? ''}`.trim() : t.dueDate;
  return t.isOverdue ? `Overdue since ${day}` : `Due ${day}`;
}

/** Due choices for a team task: the server computes the date from amount + unit + anchor "created". */
export const DUE_CHOICES: readonly { value: string; label: string; when: { amount?: number; unit?: string; anchor: string } }[] = [
  { value: 'none', label: 'No due date', when: { anchor: 'none' } },
  { value: '0d', label: 'Today', when: { amount: 0, unit: 'day', anchor: 'created' } },
  { value: '1d', label: 'Tomorrow', when: { amount: 1, unit: 'day', anchor: 'created' } },
  { value: '3d', label: 'In 3 days', when: { amount: 3, unit: 'day', anchor: 'created' } },
  { value: '1w', label: 'In 1 week', when: { amount: 1, unit: 'week', anchor: 'created' } },
  { value: '2w', label: 'In 2 weeks', when: { amount: 2, unit: 'week', anchor: 'created' } },
  { value: '1m', label: 'In 1 month', when: { amount: 1, unit: 'month', anchor: 'created' } },
];

export interface NewTaskInput {
  kind: string;
  description: string;
  performerType: TaskRole;
  due: string;
  priority: 'routine' | 'urgent';
}

/** The create body for a team task (live on save, no visit). Returns an error line instead when the input is not valid. */
export function buildCreateBody(input: NewTaskInput): { body: Json } | { error: string } {
  const description = input.description.trim();
  if (description === '') return { error: 'Say what needs doing.' };
  if (description.length > MAX_DESCRIPTION) return { error: `At most ${MAX_DESCRIPTION} characters.` };
  if (!TASK_KINDS.some((k) => k.code === input.kind)) return { error: 'Choose what kind of task this is.' };
  if (!isTaskRole(input.performerType)) return { error: 'Choose who it is for.' };
  const due = DUE_CHOICES.find((d) => d.value === input.due);
  if (!due) return { error: 'Choose when it is due.' };
  return {
    body: {
      origin: 'team',
      kind: input.kind,
      description,
      performerType: input.performerType,
      priority: input.priority,
      when: due.when,
    },
  };
}

/** A failed task call, in words. Never quotes a body: it may carry patient data. Reads the store's stable `code`. */
export function describeTaskError(err: unknown): string {
  if (err instanceof Error && err.name === 'CareModelReconcileError')
    return 'The task was marked done, but the care model did not record the item and the task could not be reopened. Tick the item from the patient record.';
  if (err instanceof Error && err.name === 'CareModelTickError')
    return 'The care model did not record this item (or its step is still being read), so the task stays open. Try again in a minute.';
  const status = isObject(err) && typeof err.status === 'number' ? err.status : undefined;
  const code = isObject(err) && typeof err.body === 'string' ? codeOf(err.body) : null;
  switch (code) {
    case 'duplicate-open-task':
      return 'An open task already covers this for this patient.';
    case 'version-conflict':
      return 'Someone else changed this task. The list was read again: check it and try again.';
    case 'task-action-not-permitted':
      return 'You are not allowed to do this on this task.';
    case 'invalid-transition':
      return 'This task can no longer be changed that way. The list was read again.';
    case 'store-unavailable':
      return 'Tasks are not available on this environment yet.';
    default:
      break;
  }
  if (status === undefined) return 'The task could not be saved.';
  if (status === 0) return 'The task service could not be reached. Try again.';
  if (status === 401) return 'Your session has expired. Sign in again, then retry.';
  if (status === 403) return 'Your sign-in is not allowed to change tasks.';
  if (status === 404) return 'This task or patient was not found in your organisation.';
  return `The task service could not save this (HTTP ${status}).`;
}

function codeOf(body: string): string | null {
  try {
    const parsed: unknown = JSON.parse(body);
    return isObject(parsed) ? str(parsed.code) : null;
  } catch {
    return null;
  }
}
